/**
 * EmailJS Integration for Quotation Forms
 *
 * This script handles EmailJS integration for quotation forms, including:
 * 1. Form submission interception
 * 2. Product information extraction from quotation cart
 * 3. Email sending to admin and client
 * 4. Cart clearing after successful submission
 *
 * Dependencies:
 * - EmailJS library (https://cdn.jsdelivr.net/npm/@emailjs/browser@4/dist/email.min.js)
 * - Quotation cart system (quotation-cart.js)
 */

(function () {
  "use strict";

  // ============================================
  // CONFIGURATION - Reemplaza con tus valores
  // ============================================
  const CONFIG = {
    PUBLIC_KEY: "d_58bwS9omzoCi8cS", // Tu User ID de EmailJS
    SERVICE_ID: "service_41uow5c", // Tu Service ID
    TEMPLATE_ID_ADMIN: "template_pjbah78", // Template para notificar al sistema
    TEMPLATE_ID_CLIENT: "template_aw5frum", // Template para confirmar al cliente

    // Logo y contacto de la empresa
    LOGO_URL:
      "https://cdn.shopify.com/s/files/1/0699/0243/4466/files/Copargo_Control_H_1_e3b22cf4-0a0d-48af-bbc3-1dd2455e1a2a.png?v=1767974465", // URL de tu logo (recomendado: 180px de ancho)
    WHATSAPP_NUMBER: "56999821304", // Número de WhatsApp con código de país (sin +)
    // Si true, no llama a EmailJS; en su lugar abre una vista previa en nueva pestaña
    DRY_RUN: false,
  };

  // ============================================
  // Inicializar EmailJS
  // ============================================
  if (typeof emailjs !== "undefined") {
    emailjs.init(CONFIG.PUBLIC_KEY);
  } else {
    console.error("EmailJS library not loaded");
    return;
  }

  // ============================================
  // Función para obtener el dominio actual de la tienda
  // ============================================
  function getShopDomain() {
    // Intentar obtener el dominio de varias formas
    let domain = "";

    // 1. Desde window.Shopify.shop (Probablemente lo más confiable para el dominio base .myshopify.com)
    if (window.Shopify && window.Shopify.shop) {
      domain = `https://${window.Shopify.shop}`;
      console.log("✅ Dominio detectado desde Shopify.shop:", domain);
    }

    // 2. Desde window.location (para capturar dominios personalizados)
    if (window.location && window.location.hostname) {
      const hostname = window.location.hostname;

      // No usar localhost o 127.0.0.1 como dominio de la tienda para correos
      if (
        !hostname.includes("127.0.0.1") &&
        !hostname.includes("localhost") &&
        !hostname.includes("0.0.0.0")
      ) {
        domain = `https://${hostname}`;
        console.log(
          "✅ Dominio personalizado detectado desde window.location:",
          domain,
        );
      }
    }

    // 3. Fallback a dominio configurado manualmente
    if (!domain) {
      domain = "https://copargo-control.myshopify.com";
      console.log("⚠️ Usando dominio fallback:", domain);
    }

    return domain;
  }

  // ============================================
  // Función para convertir URLs locales a Shopify CDN
  // ============================================
  function convertToShopifyCDN(url) {
    if (!url) return "";

    console.log("🔍 Procesando URL de imagen:", url);

    // Limpiar URL de caracteres codificados y atributos extra
    let cleanUrl = url;

    // Si contiene originalsrc, extraer el valor correcto
    if (url.includes("originalsrc=")) {
      const match = url.match(/originalsrc="([^"]+)"/);
      if (match) {
        cleanUrl = match[1];
        console.log("🔄 URL extraída de originalsrc:", cleanUrl);
      }
    }

    // Decodificar caracteres HTML múltiples veces (por doble codificación)
    let previousUrl = "";
    while (cleanUrl !== previousUrl) {
      previousUrl = cleanUrl;
      cleanUrl = cleanUrl
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&amp;/g, "&"); // Repetir por si hay doble codificación
    }

    // Limpiar espacios extra y caracteres problemáticos
    cleanUrl = cleanUrl.trim();

    // Obtener dominio actual de la tienda
    const shopDomain = getShopDomain();

    // Si comienza con //, agregar https con dominio actual
    if (cleanUrl.startsWith("//")) {
      const finalUrl = "https:" + cleanUrl;
      console.log("✅ URL con // convertida:", finalUrl);
      return finalUrl;
    }

    // Si ya es una URL de Shopify CDN completa, dejarla tal cual
    // (Es mejor usar el CDN directo en correos que pasar por el dominio de la tienda)
    if (cleanUrl.startsWith("https://cdn.shopify.com")) {
      console.log("✅ URL de CDN preservada:", cleanUrl);
      return cleanUrl;
    }

    // Si es URL de Shopify con otros dominios (shop.com, etc.)
    if (
      cleanUrl.includes("shopify.com") ||
      cleanUrl.includes(".myshopify.com")
    ) {
      console.log("✅ URL de Shopify detectada:", cleanUrl);
      return cleanUrl;
    }

    // Si es URL relativa (empieza con /)
    if (cleanUrl.startsWith("/") && !cleanUrl.startsWith("//")) {
      // Convertir a URL absoluta usando el dominio actual de la tienda
      const finalUrl = shopDomain + cleanUrl;
      console.log(
        "🔄 URL relativa convertida a absoluta (dominio actual):",
        finalUrl,
      );
      return finalUrl;
    }

    // Si es URL local (127.0.0.1 o localhost), convertir a Shopify CDN
    if (cleanUrl.includes("127.0.0.1") || cleanUrl.includes("localhost")) {
      console.log("🔄 Convirtiendo URL local a Shopify CDN");
      // Extraer el path completo (incluyendo /cdn/shop/files/, etc.)
      const path = cleanUrl.replace(/^https?:\/\/[^\/]+/, "");
      const finalUrl = shopDomain + path;
      console.log("✅ URL local convertida:", finalUrl);
      return finalUrl;
    }

    // Para cualquier otro caso, devolver la URL original
    console.log("⚠️ URL no modificada, usando original:", cleanUrl);
    return cleanUrl;
  }

  // ============================================
  // Extraer información de productos del carrito
  // ============================================
  function extractProductInfo(form) {
    const shopDomain = getShopDomain();

    // PRIMERO: Intentar obtener productos del campo oculto (flujo 1 - lista de cotización)
    const productsField = form.querySelector('[name="contact[products]"]');
    if (productsField && productsField.value) {
      try {
        const cartData = JSON.parse(productsField.value);

        if (cartData.items && cartData.items.length > 0) {
          console.log(
            "📦 Usando productos del campo oculto (flujo 1):",
            cartData.items.length,
            "productos",
          );
          let productsHTML =
            '<table style="width: 100%; border-collapse: collapse;">';

          cartData.items.forEach((item, index) => {
            // Obtener imagen del producto
            console.log(
              `📸 Procesando imagen para ${item.title}:`,
              item.featured_image,
            );
            let imgSrc = item.featured_image || "";
            if (imgSrc) {
              imgSrc = convertToShopifyCDN(imgSrc);
              console.log(`✅ Imagen convertida:`, imgSrc);
            } else {
              console.warn(`⚠️ No hay imagen para ${item.title}`);
            }

            const imageCell = imgSrc
              ? `<td style="padding: 10px; width: 80px; vertical-align: middle;">
                  <img src="${imgSrc}" alt="${item.title}" style="width: 60px; height: 60px; object-fit: cover; border-radius: 4px; display: block;" onerror="this.style.display='none'; this.nextElementSibling.style.display='block';">
                  <div style="width:60px; height:60px; background:#e6e6e6; border-radius:4px; display:none; align-items:center; justify-content:center; color:#999; font-size:10px; text-align:center;">Sin imagen</div>
                </td>`
              : `<td style="padding: 10px; width: 80px; vertical-align: middle;">
                  <div style="width:60px; height:60px; background:#e6e6e6; border-radius:4px; display:flex; align-items:center; justify-content:center; color:#999; font-size:10px; text-align:center;">Sin imagen</div>
                </td>`;

            // Construir URL absoluta del producto
            let productUrl = "#";
            if (item.url) {
              productUrl = item.url.startsWith("http")
                ? item.url
                : shopDomain + (item.url.startsWith("/") ? "" : "/") + item.url;
            } else if (item.handle) {
              productUrl = shopDomain + "/products/" + item.handle;
            }

            productsHTML += `
              <tr style="border-bottom: 1px solid #e0e0e0;">
                ${imageCell}
                <td style="padding: 10px; vertical-align: middle;">
                  <a href="${productUrl}" target="_blank" style="text-decoration: none; color: #333;">
                    <strong style="font-size: 14px;">${item.title}</strong>
                  </a><br>
                  ${item.sku ? `<span style="font-size: 11px; color: #666;">SKU: ${item.sku}</span><br>` : ""}
                  <span style="color: #666; font-size: 13px;">Cantidad: ${item.quantity || 1}</span>
                </td>
              </tr>`;
          });

          productsHTML += "</table>";
          return productsHTML;
        }
      } catch (error) {
        console.error("Error al procesar productos del campo oculto:", error);
      }
    }

    // SEGUNDO: Intentar obtener productos del carrito normal (flujo 2 - main cart)
    const cartItems = document.querySelectorAll(".hdt-cart-item");

    if (cartItems.length > 0) {
      console.log(
        "🛒 Usando productos del main cart (flujo 2):",
        cartItems.length,
        "items",
      );
      let productsHTML =
        '<table style="width: 100%; border-collapse: collapse;">';
      let hasProducts = false;

      cartItems.forEach((item, index) => {
        // Buscar el título del producto
        const titleElement = item.querySelector(".hdt-page-cart__title");
        const productName = titleElement
          ? titleElement.textContent.trim()
          : null;

        // Si no hay nombre, saltar este item (puede ser gift wrap u otro)
        if (!productName) return;

        // Buscar imagen
        const imgElement = item.querySelector("img");
        let imgSrc = imgElement ? imgElement.getAttribute("src") : "";
        if (imgSrc) {
          imgSrc = convertToShopifyCDN(imgSrc);
        }

        // Buscar cantidad
        const qtyInput = item.querySelector('input[name="updates[]"]');
        const quantity = qtyInput ? qtyInput.value : "1";

        // Buscar URL
        const linkElement = item.querySelector("a[href]");
        let productUrl = "#";
        if (linkElement) {
          const href = linkElement.getAttribute("href");
          productUrl = href.startsWith("http")
            ? href
            : shopDomain + (href.startsWith("/") ? "" : "/") + href;
        }

        const imageCell = imgSrc
          ? `<td style="padding: 10px; width: 80px; vertical-align: middle;">
              <img src="${imgSrc}" alt="${productName}" style="width: 60px; height: 60px; object-fit: cover; border-radius: 4px; display: block;" onerror="this.style.display='none'; this.nextElementSibling.style.display='block';">
              <div style="width:60px; height:60px; background:#e6e6e6; border-radius:4px; display:none; align-items:center; justify-content:center; color:#999; font-size:10px; text-align:center;">Sin imagen</div>
            </td>`
          : `<td style="padding: 10px; width: 80px; vertical-align: middle;">
              <div style="width:60px; height:60px; background:#e6e6e6; border-radius:4px; display:flex; align-items:center; justify-content:center; color:#999; font-size:10px; text-align:center;">Sin imagen</div>
            </td>`;

        productsHTML += `
          <tr style="border-bottom: 1px solid #e0e0e0;">
            ${imageCell}
            <td style="padding: 10px; vertical-align: middle;">
              <a href="${productUrl}" target="_blank" style="text-decoration: none; color: #333;">
                <strong style="font-size: 14px;">${productName}</strong>
              </a><br>
              <span style="color: #666; font-size: 13px;">Cantidad: ${quantity}</span>
            </td>
          </tr>`;

        hasProducts = true;
      });

      productsHTML += "</table>";
      return hasProducts ? productsHTML : "";
    }

    // TERCERO: Intentar obtener producto de la página actual (flujo individual)
    const productTitle = getProductTitleFromPage();
    if (productTitle) {
      console.log("📄 Usando producto de la página actual:", productTitle);
      const productImage = getProductImageFromPage();
      let imgSrc = productImage ? convertToShopifyCDN(productImage) : "";

      const imageCell = imgSrc
        ? `<td style="padding: 10px; width: 80px; vertical-align: middle;">
            <img src="${imgSrc}" alt="${productTitle}" style="width: 60px; height: 60px; object-fit: cover; border-radius: 4px; display: block;" onerror="this.style.display='none'; this.nextElementSibling.style.display='block';">
            <div style="width:60px; height:60px; background:#e6e6e6; border-radius:4px; display:none; align-items:center; justify-content:center; color:#999; font-size:10px; text-align:center;">Sin imagen</div>
          </td>`
        : `<td style="padding: 10px; width: 80px; vertical-align: middle;">
            <div style="width:60px; height:60px; background:#e6e6e6; border-radius:4px; display:flex; align-items:center; justify-content:center; color:#999; font-size:10px; text-align:center;">Sin imagen</div>
          </td>`;

      const productUrl = window.location.href;

      return `
        <table style="width: 100%; border-collapse: collapse;">
          <tr style="border-bottom: 1px solid #e0e0e0;">
            ${imageCell}
            <td style="padding: 10px; vertical-align: middle;">
              <a href="${productUrl}" target="_blank" style="text-decoration: none; color: #333;">
                <strong style="font-size: 14px;">${productTitle}</strong>
              </a><br>
              <span style="color: #666; font-size: 13px;">Cantidad: 1</span>
            </td>
          </tr>
        </table>`;
    }

    // Fallback: devolver cadena vacía si no hay productos
    console.log("⚠️ No se encontraron productos en ningún flujo");
    return "";
  }

  // ============================================
  // Función principal para manejar envío de formularios
  // ============================================
  window.handleQuotationSubmit = function (event) {
    console.log(
      "🚀 handleQuotationSubmit triggered via " +
        (event ? event.type : "manual call"),
    );
    // Soporta submit events y clicks sobre el botón. Evitar navegación por defecto cuando sea posible.
    if (event && typeof event.preventDefault === "function") {
      try {
        event.preventDefault();
      } catch (e) {}
    }

    // Determinar formulario objetivo
    let form = null;
    if (event && event.target) {
      const t = event.target;
      if (t.tagName && t.tagName.toLowerCase() === "form") {
        form = t;
      } else if (t.closest) {
        form = t.closest("form");
      }
    }
    if (!form) form = document.querySelector('form[action*="/contact"]');
    if (!form) {
      console.error("handleQuotationSubmit: formulario no encontrado");
      return;
    }

    const submitButton =
      form.querySelector(".hdt-btn-submit-quotation") || null;
    const originalButtonText = submitButton ? submitButton.textContent : "";
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = "Enviando...";
    }

    // Extraer datos del formulario (proteger con try/catch)
    let formData = {};
    try {
      formData = {
        nombre:
          form.querySelector('[name="contact[nombre]"]')?.value ||
          form.querySelector('[name="contact[name]"]')?.value ||
          "",
        correo:
          form.querySelector('[name="contact[correo]"]')?.value ||
          form.querySelector('[name="contact[email]"]')?.value ||
          "",
        telefono:
          form.querySelector('[name="contact[telefono]"]')?.value ||
          form.querySelector('[name="contact[phone]"]')?.value ||
          "",
        razon_social:
          form.querySelector('[name="contact[razon_social]"]')?.value || "",
        rut: form.querySelector('[name="contact[rut]"]')?.value || "",
        direccion:
          form.querySelector('[name="contact[direccion]"]')?.value || "",
        giro: form.querySelector('[name="contact[giro]"]')?.value || "",
        productos: extractProductInfo(form),
        logo_url: CONFIG.LOGO_URL,
        whatsapp_number: CONFIG.WHATSAPP_NUMBER,
      };
    } catch (error) {
      console.error("Error extrayendo datos del formulario:", error);
      showErrorMessage(form, "Error al procesar el formulario");
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = originalButtonText;
      }
      return;
    }

    // Enviar correos (o simular si es DRY_RUN)
    let sendPromise;

    if (CONFIG.DRY_RUN) {
      console.log("DRY RUN: Quotation data", formData);
      previewEmail(formData);
      // Simular éxito inmediato
      sendPromise = Promise.resolve([{ text: "OK (DRY RUN)" }]);
    } else {
      console.log("🔧 Enviando correos reales...");
      console.log("📋 CONFIG.SERVICE_ID:", CONFIG.SERVICE_ID);
      console.log("📋 CONFIG.TEMPLATE_ID_ADMIN:", CONFIG.TEMPLATE_ID_ADMIN);
      console.log("📋 CONFIG.TEMPLATE_ID_CLIENT:", CONFIG.TEMPLATE_ID_CLIENT);
      console.log("📋 emailjs disponible:", typeof emailjs !== "undefined");

      // Enviar correos en paralelo
      sendPromise = Promise.all([
        emailjs
          .send(CONFIG.SERVICE_ID, CONFIG.TEMPLATE_ID_ADMIN, formData)
          .then((r) => {
            console.log("✅ Email ADMIN enviado exitosamente:", r);
            return r;
          })
          .catch((e) => {
            console.error("Error enviando email ADMIN:", e);
            console.error("Detalles del error ADMIN:", e);
            throw e;
          }),
        emailjs
          .send(CONFIG.SERVICE_ID, CONFIG.TEMPLATE_ID_CLIENT, formData)
          .then((r) => {
            console.log("✅ Email CLIENTE enviado exitosamente:", r);
            return r;
          })
          .catch((e) => {
            console.error("Error enviando email CLIENTE:", e);
            console.error("Detalles del error CLIENTE:", e);
            throw e;
          }),
      ]);
    }

    sendPromise
      .then(function (responses) {
        showSuccessMessage(form);

        if (window.quotationCart) {
          try {
            window.quotationCart.clearCart(true);
            window.quotationCart.updateUI();
          } catch (error) {
            console.error("Error al limpiar carrito de cotización:", error);
          }
        }

        // Disparar evento para que otros componentes (página de cotización) limpien su UI
        window.dispatchEvent(
          new CustomEvent("quotationSubmitSuccess", {
            detail: { success: true },
          }),
        );

        setTimeout(function () {
          form.reset();
          const modal = form.closest("hdt-modal");
          if (modal && typeof modal.hide === "function") modal.hide();
          const dialog = form.closest("dialog");
          if (dialog && dialog.open) dialog.close();
        }, 5000);
      })
      .catch(function (error) {
        console.error("Error general al enviar emails:", error);
        showErrorMessage(
          form,
          "Error al enviar la cotización. Por favor, inténtalo de nuevo.",
        );
      })
      .finally(function () {
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.textContent = originalButtonText;
        }
      });
  };

  // ============================================
  // Funciones auxiliares
  // ============================================
  function getProductTitleFromPage() {
    // Intentar obtener desde variables globales de Shopify
    try {
      if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta) {
        const meta = window.ShopifyAnalytics.meta;
        if (meta.product && meta.product.title) {
          return meta.product.title;
        }
      }
    } catch (e) {}

    // Intentar desde el título de la página
    try {
      if (document.title) {
        return document.title.replace(/\s+-\s+.*$/, "").trim();
      }
    } catch (e) {}

    return null;
  }

  function getProductImageFromPage() {
    try {
      // Buscar imagen principal del producto
      const productImage = document.querySelector(
        ".product-featured-img img, .product-single__media img, .hdt-product-media img",
      );
      if (productImage && productImage.src) {
        return productImage.src;
      }
    } catch (e) {}

    return null;
  }

  function showSuccessMessage(form) {
    const message =
      "¡Solicitud recibida con éxito! 🚀\nHemos enviado un respaldo a tu correo. Nuestro equipo comercial te contactará a la brevedad.";

    if (window.quotationCart && window.quotationCart.showNotification) {
      window.quotationCart.showNotification(message, "success");
    } else {
      showMessage(form, message, "success");
    }
  }

  function showErrorMessage(form, message) {
    const defaultMessage =
      "Hubo un problema al enviar tu solicitud.\nPor favor verifica tu conexión o contáctanos por WhatsApp si el problema persiste.";

    if (window.quotationCart && window.quotationCart.showNotification) {
      window.quotationCart.showNotification(message || defaultMessage, "error");
    } else {
      showMessage(form, message || defaultMessage, "error");
    }
  }

  function showMessage(form, message, type) {
    // Buscar o crear contenedor de mensajes
    let container = form.querySelector(".quotation-message-container");
    if (!container) {
      container = document.createElement("div");
      container.className = "quotation-message-container";
      form.insertBefore(container, form.firstChild);
    }

    const isSuccess = type === "success";
    container.innerHTML = `
      <div style="
        padding: 15px;
        margin: 10px 0;
        border-radius: 4px;
        background-color: ${isSuccess ? "#d4edda" : "#f8d7da"};
        color: ${isSuccess ? "#155724" : "#721c24"};
        border: 1px solid ${isSuccess ? "#c3e6cb" : "#f5c6cb"};
        font-size: 14px;
        text-align: center;
      ">
        ${message}
      </div>
    `;
  }

  function previewEmail(data) {
    const emailContent = `
      <html>
        <head><title>Vista Previa de Email</title></head>
        <body style="font-family: Arial, sans-serif; padding: 20px;">
          <h1>Vista Previa - Cotización</h1>
          <p><strong>Nombre:</strong> ${data.nombre}</p>
          <p><strong>Correo:</strong> ${data.correo}</p>
          <p><strong>Teléfono:</strong> ${data.telefono}</p>
          <p><strong>Razón Social:</strong> ${data.razon_social}</p>
          <p><strong>RUT:</strong> ${data.rut}</p>
          <p><strong>Dirección:</strong> ${data.direccion}</p>
          <p><strong>Giro:</strong> ${data.giro}</p>
          <hr>
          <h2>Productos Solicitados</h2>
          ${data.productos}
        </body>
      </html>
    `;

    const newWindow = window.open("", "_blank", "width=800,height=600");
    if (newWindow) {
      newWindow.document.write(emailContent);
      newWindow.document.close();
    }
  }

  // ============================================
  // Inicializar event listeners cuando el DOM esté listo
  // ============================================
  function init() {
    // Buscar todos los formularios de cotización
    const quotationForms = document.querySelectorAll(
      'form[action*="/contact"]',
    );

    quotationForms.forEach(function (form) {
      // Solo interceptar formularios que tengan el botón de cotización
      const hasQuotationButton = form.querySelector(
        ".hdt-btn-submit-quotation",
      );

      if (hasQuotationButton) {
        // Agregar clase para identificar formularios de cotización
        form.classList.add("quotation-form-with-products");
        console.log(
          "✅ Clase quotation-form-with-products agregada a formulario estático",
        );

        form.addEventListener("submit", window.handleQuotationSubmit);
      }
    });

    // Interceptar clicks en botones que abren el formulario (prevent navigation to /contact/captcha)
    document.addEventListener(
      "click",
      function (e) {
        try {
          const btn =
            e.target.closest && e.target.closest(".hdt-btn-submit-quotation");
          if (btn) {
            console.log("🔧 Botón de cotización clickeado:", btn);

            // NO interceptar botones que estén dentro de formularios con clase "quotation-form-with-products"
            // Esos formularios tienen su propia validación en quotation-cart.js
            const parentForm = btn.closest("form");
            console.log("🔧 Formulario padre encontrado:", parentForm);
            console.log(
              "🔧 Clases del formulario:",
              parentForm ? parentForm.className : "No encontrado",
            );

            if (
              parentForm &&
              parentForm.classList.contains("quotation-form-with-products")
            ) {
              console.log(
                "🔧 Botón dentro de formulario dinámico, ignorando interceptación global",
              );
              console.log("🔧 Dejando que el formulario se maneje normalmente");
              return; // Dejar que el formulario se maneje normalmente
            }

            console.log(
              "🔧 Interceptando click en botón de cotización estático",
            );
            e.preventDefault();
            window.handleQuotationSubmit(e);
          }
        } catch (err) {
          console.error("Error en event listener global:", err);
          // ignore
        }
      },
      true,
    );
  }

  // Inicializar cuando el DOM esté listo
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
